// buildLibraryCaptureActions.js — empty-state Capture rows for Project Info sidebar

/** Types already covered by the fixed system Capture actions (or media aliases). */
export const LIBRARY_SYSTEM_CAPTURE_TYPE_KEYS = Object.freeze(new Set([
  'FILE',
  'DOCUMENT',
  'PHOTO',
  'VIDEO',
  'SNAPSHOT',
  'RECORDING',
  'CAMERA',
  'ATTACH',
  'NOTE',
  'SKETCH',
]));

/** Fixed empty-state Capture actions (every base). */
export const LIBRARY_SYSTEM_CAPTURE_ACTIONS = Object.freeze([
  {
    key: 'ATTACH',
    label: 'Add Files',
    iconKey: 'attach_file',
    kind: 'files',
  },
  {
    key: 'CAMERA',
    label: 'Add Photo/Video',
    iconKey: 'photo_camera',
    kind: 'camera',
  },
  {
    key: 'NOTE',
    label: 'Add Note',
    iconKey: 'sticky_note_2',
    kind: 'type',
  },
  {
    key: 'SKETCH',
    label: 'Add Sketch',
    iconKey: 'draw',
    kind: 'type',
  },
]);

function normalizeTypeKey(value) {
  return String(value || '').trim();
}

function normalizeTypeKeyUpper(value) {
  return normalizeTypeKey(value).toUpperCase();
}

function isSystemCaptureTypeKey(typeKey) {
  return LIBRARY_SYSTEM_CAPTURE_TYPE_KEYS.has(normalizeTypeKeyUpper(typeKey));
}

/**
 * True for opaque keys (e.g. Firestore auto-ids) that must never appear as UI labels.
 *
 * @param {string} value
 * @returns {boolean}
 */
export function looksLikeOpaqueTypeKey(value) {
  const key = normalizeTypeKey(value);
  if (!key) return true;
  // Random / auto-id tokens: long unbroken alnum (e.g. HNL5PXDAJONWL14VYQ6H)
  return /^[A-Za-z0-9]{16,}$/.test(key);
}

/**
 * True for placeholder / junk labels that should never appear as Capture actions.
 *
 * @param {string} value
 * @returns {boolean}
 */
export function isPlaceholderCaptureLabel(value) {
  const label = String(value || '').trim().toLowerCase();
  if (!label) return true;
  if (label === 'untitled' || label === 'untitled collection' || label === 'untitled database') {
    return true;
  }
  if (/^untitled\b/.test(label)) return true;
  return false;
}

/**
 * Prefer a human singular label; never use an opaque type key as the label.
 *
 * @param {object|null} typeDef
 * @param {string} typeKey
 * @param {string} [fallbackLabel]
 * @returns {string}
 */
export function resolveCaptureTypeLabel(typeDef, typeKey, fallbackLabel = '') {
  const fromDef = String(typeDef?.label || '').trim();
  if (fromDef && !isPlaceholderCaptureLabel(fromDef)) return fromDef;

  const fromFallback = String(fallbackLabel || '').trim();
  if (
    fromFallback
    && !isPlaceholderCaptureLabel(fromFallback)
    && normalizeTypeKeyUpper(fromFallback) !== normalizeTypeKeyUpper(typeKey)
  ) {
    return fromFallback;
  }
  if (
    fromFallback
    && !isPlaceholderCaptureLabel(fromFallback)
    && !looksLikeOpaqueTypeKey(fromFallback)
  ) {
    return fromFallback;
  }

  // Type exists but only has a placeholder label — do not invent a Capture name from the key.
  if (typeDef && fromDef && isPlaceholderCaptureLabel(fromDef)) {
    return '';
  }

  const key = normalizeTypeKey(typeKey);
  if (key && !looksLikeOpaqueTypeKey(key) && /^[A-Za-z][A-Za-z0-9_ -]*$/.test(key)) {
    // Named keys like TASK / Subcontractor — title-case lightly for display
    if (key === key.toUpperCase() && !key.includes(' ')) {
      return key.charAt(0) + key.slice(1).toLowerCase();
    }
    return key;
  }

  return '';
}

/**
 * Collect pack / app-provisioned type refs from base app instances.
 *
 * @param {object[]} appInstances
 * @returns {{ typeKey: string, label: string }[]}
 */
export function collectAppProvisionedTypeRefs(appInstances = []) {
  const byKey = new Map();

  (Array.isArray(appInstances) ? appInstances : []).forEach((instance) => {
    (Array.isArray(instance?.databases) ? instance.databases : []).forEach((row) => {
      const typeKey = normalizeTypeKey(row?.typeKey || row?.type);
      if (!typeKey) return;
      if (isSystemCaptureTypeKey(typeKey)) return;

      const label = String(row?.label || row?.labelSingular || '').trim();
      const existing = byKey.get(typeKey);
      if (!existing || (!existing.label && label)) {
        byKey.set(typeKey, { typeKey, label });
      }
    });
  });

  return [...byKey.values()];
}

/**
 * @deprecated Use collectAppProvisionedTypeRefs
 * @param {object[]} appInstances
 * @returns {string[]}
 */
export function collectAppProvisionedTypeKeys(appInstances = []) {
  return collectAppProvisionedTypeRefs(appInstances).map((row) => row.typeKey);
}

/**
 * Collect custom (non-app, non-system) type refs from Library-visible databases.
 *
 * @param {object[]} databases
 * @param {object} options
 * @returns {{ typeKey: string, label: string }[]}
 */
export function collectCustomCaptureTypeRefs(databases = [], {
  getType,
  isAppProvisioned,
} = {}) {
  const byKey = new Map();

  (Array.isArray(databases) ? databases : []).forEach((row) => {
    const database = row?.database && typeof row.database === 'object' ? row.database : row;
    const typeKey = normalizeTypeKey(database?.type || row?.type);
    if (!typeKey) return;
    if (isSystemCaptureTypeKey(typeKey)) return;

    const typeDef = typeof getType === 'function' ? getType(typeKey) : null;
    if (typeof isAppProvisioned === 'function' && isAppProvisioned(typeDef, database)) {
      return;
    }

    const label = String(row?.label || database?.title || '').trim();
    byKey.set(typeKey, { typeKey, label });
  });

  return [...byKey.values()];
}

/**
 * @deprecated Use collectCustomCaptureTypeRefs
 */
export function collectCustomCaptureTypeKeys(databases = [], options = {}) {
  return collectCustomCaptureTypeRefs(databases, options).map((row) => row.typeKey);
}

/**
 * Build labeled Capture action rows for pack or custom groups.
 * Skips types with no live type def or no resolvable human label.
 *
 * @param {{ typeKey: string, label?: string }[]|string[]} typeRefs
 * @param {object} options
 * @returns {{ key: string, typeKey: string, label: string, iconKey: string, kind: 'type' }[]}
 */
export function buildTypedCaptureActions(typeRefs = [], {
  getType,
  requireTypeDef = true,
} = {}) {
  const rows = (Array.isArray(typeRefs) ? typeRefs : [])
    .map((ref) => {
      const typeKey = normalizeTypeKey(
        typeof ref === 'string' ? ref : ref?.typeKey,
      );
      if (!typeKey) return null;

      const fallbackLabel = typeof ref === 'string'
        ? ''
        : String(ref?.label || '').trim();

      const typeDef = typeof getType === 'function' ? getType(typeKey) : null;
      // Also try uppercase lookup for legacy defs keyed in ALL_CAPS
      const typeDefResolved = typeDef
        || (typeof getType === 'function' ? getType(normalizeTypeKeyUpper(typeKey)) : null);

      // Stale app-instance rows can outlive deleted types/collections — require a live def.
      if (requireTypeDef && !typeDefResolved) return null;

      const singular = resolveCaptureTypeLabel(
        typeDefResolved,
        typeKey,
        typeDefResolved ? fallbackLabel : '',
      );
      if (!singular || isPlaceholderCaptureLabel(singular)) return null;

      const iconKey = (typeof typeDefResolved?.iconKey === 'string' && typeDefResolved.iconKey.trim())
        || 'deployed_code';

      return {
        key: typeKey,
        typeKey,
        label: `Add ${singular}`,
        iconKey,
        kind: 'type',
      };
    })
    .filter(Boolean);

  return rows.sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Full empty-state Capture model for the Library / Info sidebar section.
 *
 * @param {object} params
 * @returns {{ system: object[], pack: object[], custom: object[] }}
 */
export function buildLibraryCaptureActions({
  appInstances = [],
  libraryDatabases = [],
  getType,
  isAppProvisioned,
} = {}) {
  const packRefs = collectAppProvisionedTypeRefs(appInstances);
  const packKeySet = new Set(packRefs.map((row) => normalizeTypeKeyUpper(row.typeKey)));

  const customRefs = collectCustomCaptureTypeRefs(libraryDatabases, {
    getType,
    isAppProvisioned,
  }).filter((row) => !packKeySet.has(normalizeTypeKeyUpper(row.typeKey)));

  return {
    system: [...LIBRARY_SYSTEM_CAPTURE_ACTIONS],
    pack: buildTypedCaptureActions(packRefs, { getType }),
    custom: buildTypedCaptureActions(customRefs, { getType }),
  };
}
